import { Dict, formatToShortMoney } from "@verivest/shared-components";
import {
  AttributeItemModel,
  OfferingModel,
  OpportunityModel,
  SecurityModel,
} from "@verivest/shared-components/src/api/models";
import { dateToString, formatMoney } from "../../../../../utils/utils";

const FT_MINIMUM_AMOUNT = {
  name: "Minimum",
  key: "minimumAmount",
  format: "shortCurrency",
};
const FT_PREFERREDRETURN = {
  name: "Preferred Return",
  key: "preferredReturn",
  format: "percentage",
  defaultsTo: "None",
};
const FT_PROFITSPLIT = {
  name: "Investor Profit Split",
  key: "profitSplit",
  format: "percentage",
};
const FT_IRRNETOFFEES = {
  name: "IRR, Net of Fees",
  key: "irrNetOfFees",
  format: "percentage",
};
const FT_EQUITYMULTIPLENET = {
  name: "Equity Multiple, Net of Fees",
  key: "equityMultipleNet",
  format: "x",
};
const FT_INCEPTIONDATE = {
  name: "Inception Date",
  key: "inceptionDate",
  format: "date",
};
const FT_WINDUPDATE = {
  name: "Wind-up Date",
  key: "windupDate",
  format: "date",
  fromOpportunity: true,
};
const FT_TARGETTOTALRETURN = {
  name: "Target Total Return",
  key: "targetTotalReturn",
  format: "percentage",
};
const FT_HOLDPERIOD = { name: "Hold Period", key: "holdPeriod", format: "mo" };
const FT_TARGETINCOMERETURN = {
  name: "Target Income Return",
  key: "targetIncomeReturn",
  format: "percentage",
};
const FT_DISTRIBUTIONFREQUENCY = {
  name: "Distribution Frequency",
  key: "distributionFrequency",
  format: "text",
};

const FT_TARGETIRR = {
  name: "Target IRR",
  key: "targetIrr",
  format: "percentage",
};
const FT_PROFITSHARE = {
  name: "Profit Share",
  key: "profitShare",
  format: "percentage",
};
const FT_TARGETEQUITYMULTIPLE = {
  name: "Target Equity Multiple",
  key: "targetEquityMultiple",
  format: "x",
};

const FT_YEAR1CASH = {
  name: "Year 1 Cash",
  key: "year1Cash",
  format: "percentage",
};
const FT_TOTALPREFERREDRETURN = {
  name: "Total Preferred Return",
  key: "totalPreferredReturn",
  format: "percentage",
};
const FT_CURRENTPREFERREDRETURN = {
  name: "Current Preferred Return",
  key: "currentPreferredReturn",
  format: "percentage",
};
const FT_STABILIZEDCASHONCASH = {
  name: "Stabilized Cash on Cash",
  key: "stabilizedCashOnCash",
  format: "percentage",
};
const FT_MINSUBORDINATEDEQUITY = {
  name: "Min. Subordinated Equity",
  key: "minimumSubordinatedEquity",
  format: "percentage",
};
const FT_RATE = { name: "Note Rate", key: "rate", format: "percentage100" };
const dataManagementDefinition = [
  {
    status: ["Full Cycle"],
    offeringType: ["Equity", "Common Equity"],
    investmentProgramType: [
      "Open-End Fund",
      "Open-Ended Fund",
      "Close-End Fund",
      "SPV",
      "Syndication",
    ],
    fields: [
      FT_MINIMUM_AMOUNT,
      FT_PREFERREDRETURN,
      FT_PROFITSPLIT,
      FT_IRRNETOFFEES,
      FT_EQUITYMULTIPLENET,
      FT_INCEPTIONDATE,
      FT_WINDUPDATE,
    ],
  },
  {
    status: ["Full Cycle"],
    offeringType: ["Equity", "Common Equity"],
    investmentProgramType: ["Joint Venture"],
    fields: [
      FT_IRRNETOFFEES,
      FT_EQUITYMULTIPLENET,
      FT_INCEPTIONDATE,
      FT_WINDUPDATE,
    ],
  },
  {
    status: ["Coming Soon", "Open", "Funded"],
    offeringType: ["Equity", "Common Equity"],
    investmentProgramType: ["Open-End Fund", "Open-Ended Fund"],
    topFields: [FT_MINIMUM_AMOUNT, FT_TARGETTOTALRETURN, FT_HOLDPERIOD],
    fields: [
      FT_PREFERREDRETURN,
      FT_PROFITSPLIT,
      FT_TARGETINCOMERETURN,
      FT_DISTRIBUTIONFREQUENCY,
    ],
  },
  {
    status: ["Coming Soon", "Open", "Funded"],
    offeringType: ["Equity", "Common Equity"],
    investmentProgramType: [
      "Close-Ended Fund",
      "Close-End Fund",
      "SPV",
      "Syndication",
    ],
    topFields: [FT_MINIMUM_AMOUNT, FT_TARGETIRR, FT_HOLDPERIOD],
    fields: [
      FT_PREFERREDRETURN,
      FT_PROFITSHARE,
      FT_TARGETEQUITYMULTIPLE,
      FT_YEAR1CASH,
      FT_STABILIZEDCASHONCASH,
      FT_DISTRIBUTIONFREQUENCY,
    ],
  },
  {
    status: ["Coming Soon", "Open", "Funded"],
    offeringType: ["Equity", "Common Equity"],
    investmentProgramType: ["Joint Venture"],
    topFields: [FT_MINIMUM_AMOUNT, FT_TARGETIRR, FT_HOLDPERIOD],
    fields: [
      FT_TARGETEQUITYMULTIPLE,
      FT_YEAR1CASH,
      FT_STABILIZEDCASHONCASH,
      FT_DISTRIBUTIONFREQUENCY,
    ],
  },
  {
    status: ["Coming Soon", "Open", "Funded"],
    offeringType: ["Preferred Equity"],
    investmentProgramType: [
      "Open-End Fund",
      "Open-Ended Fund",
      "Closed-End Fund",
      "Syndication",
      "SPV",
      "Joint Venture",
    ],
    topFields: [FT_MINIMUM_AMOUNT, FT_TOTALPREFERREDRETURN, FT_HOLDPERIOD],
    fields: [
      FT_CURRENTPREFERREDRETURN,
      FT_MINSUBORDINATEDEQUITY,
      FT_DISTRIBUTIONFREQUENCY,
    ],
  },
  {
    status: ["Full Cycle"],
    offeringType: ["Equity", "Preferred Equity"],
    investmentProgramType: [
      "Open-End Fund",
      "Open-Ended Fund",
      "Closed-End Fund",
      "Syndication",
      "SPV",
      "Joint Venture",
    ],
    fields: [
      FT_MINIMUM_AMOUNT,
      FT_IRRNETOFFEES,
      FT_EQUITYMULTIPLENET,
      FT_INCEPTIONDATE,
      FT_WINDUPDATE,
    ],
  },
  {
    status: false,
    offeringType: ["Debt", "Mezzanine Note", "Senior Note"],
    investmentProgramType: false,
    topFields: [],
    fields: [
      FT_MINIMUM_AMOUNT,
      FT_RATE,
      FT_MINSUBORDINATEDEQUITY,
      FT_HOLDPERIOD,
      FT_DISTRIBUTIONFREQUENCY,
    ],
  },
];

export const fundBuilderDefinition = {
  topFields: [FT_MINIMUM_AMOUNT, FT_TARGETIRR, FT_HOLDPERIOD],
  fields: [FT_PREFERREDRETURN, FT_PROFITSHARE],
};

export function opportunitySecurityFormatField(
  definitionField: Dict,
  security: SecurityModel,
  opportunity: OpportunityModel,
  offering: OfferingModel,
) {
  const format = definitionField.format;
  const defaultValue = definitionField.defaultsTo;

  let value;
  if (definitionField.fromOpportunity) {
    value = opportunity[definitionField.key as keyof OpportunityModel];
  } else if (definitionField.fromOffering) {
    value = offering[definitionField.key as keyof OfferingModel];
  } else {
    value = security[definitionField.key as keyof SecurityModel];
  }

  if (!value || value === 0 || value === "0") {
    return defaultValue || "—";
  }

  let v;
  switch (format) {
    case "yr":
      v = parseInt(String(value || 0));
      return v + "yr";
    case "mo":
      v = parseInt(String(value || 0));
      return v + "mo";
    case "percentage":
      return +parseFloat(String(value)).toFixed(2) + "%";
    case "percentage100":
      return (+parseFloat(String(value)) * 100).toFixed(2) + "%";
    case "x":
      return +parseFloat(String(value)).toFixed(2) + "x";
    case "currency":
      return "$" + formatMoney(String(value));
    case "shortCurrency":
      return formatToShortMoney(String(value));
    case "date":
      return dateToString(new Date(String(value)));
    default:
      return value;
  }
}

export function getDefinition(
  status: string,
  investmentPrograms: (string | null | undefined)[] = [],
  offeringType: string,
): Dict | false {
  let definition: boolean | Dict = false;

  dataManagementDefinition.forEach((d) => {
    if (d.status) {
      if (Array.isArray(d.status) && d.status.indexOf(status) === -1) {
        return;
      }
    }
    if (d.offeringType) {
      if (
        Array.isArray(d.offeringType) &&
        d.offeringType.indexOf(offeringType) === -1
      ) {
        return;
      }
    }
    if (d.investmentProgramType) {
      let found = false;
      investmentPrograms.forEach((ip) => {
        if (
          Array.isArray(d.investmentProgramType) &&
          d.investmentProgramType.indexOf(ip || "") !== -1
        ) {
          found = true;
        }
      });
      if (!found) {
        return;
      }
    }
    definition = d;
  });

  return definition;
}

export const getValidOfferings = (
  offerings: OfferingModel[],
  attributeItems: AttributeItemModel[] = [],
  status: string,
  isFundBuilder: boolean,
) => {
  const validOfferings: OfferingModel[] = [];
  offerings.forEach((o) => {
    const validSecurities: SecurityModel[] = [];
    (o.securities || []).forEach((s) => {
      if (isFundBuilder) {
        if (s.showOnCard) {
          validSecurities.push(s);
        }
        return;
      }

      const investmentPrograms = attributeItems
        .filter((element) => element.attributeType === "investment_program")
        .map((a) => a.name);
      const definition = getDefinition(
        status,
        investmentPrograms,
        o.offeringType || "",
      );
      if (definition && s.showOnCard) {
        validSecurities.push(s);
      }
    });
    if (validSecurities.length > 0) {
      const offering = {
        ...o,
        securities: validSecurities,
      };
      validOfferings.push(offering);
    }
  });

  return validOfferings;
};
